import React from 'react';

/**
 * PageMeta
 * --------
 * Per-page document metadata (title, description, OpenGraph, Twitter card)
 * using React 19's native metadata hoisting — no helmet library required.
 *
 * React 19 automatically hoists <title>, <meta> and <link> tags into the
 * document <head>, replacing any previous hoisted instance on route change.
 *
 * Defaults are configured for `apexverity.io` and the new brand identity.
 * Each page should render <PageMeta> once near the top of its tree.
 *
 * Props
 *   title       — full document title (will appear in the browser tab)
 *   description — meta + og + twitter description
 *   path        — page path (used to build the canonical og:url)
 *   image       — absolute URL of the share-preview image (default = /og-image.png)
 *   imageAlt    — alt text for the share-preview image
 */
const SITE_URL = 'https://apexverity.io';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;
const DEFAULT_IMAGE_ALT = 'APEX Verity — Governance Proof Infrastructure';

export default function PageMeta({
  title,
  description,
  path = '/',
  image = DEFAULT_IMAGE,
  imageAlt = DEFAULT_IMAGE_ALT,
}) {
  const url = `${SITE_URL}${path}`;

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* OpenGraph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="APEX Verity" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:alt" content={imageAlt} />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={imageAlt} />
    </>
  );
}
